import React, { useEffect, useRef, useState } from 'react'

const pad = (n) => String(n + 1).padStart(2, '0')

const TeaserShowcase = ({ teasers }) => {
  const [active, setActive] = useState(0)
  const [progress, setProgress] = useState(0)
  const [playing, setPlaying] = useState(true)
  const [muted, setMuted] = useState(true)
  const [inView, setInView] = useState(false)
  const stageRef = useRef(null)
  const videoRef = useRef(null)
  const listRef = useRef(null)

  const current = teasers[active]

  // Only play while the showcase is on screen.
  useEffect(() => {
    const el = stageRef.current
    if (!el || !('IntersectionObserver' in window)) {
      setInView(true)
      return
    }
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.35 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    if (inView && playing) v.play().catch(() => setPlaying(false))
    else v.pause()
  }, [inView, playing, active])

  // Beside the video the list scrolls on its own; keep the active teaser in view.
  useEffect(() => {
    const list = listRef.current
    if (!list || list.scrollHeight <= list.clientHeight) return
    const item = list.children[active]
    list.scrollTo({ top: item.offsetTop - 4, behavior: 'smooth' })
  }, [active])

  const select = (i) => {
    setActive(i)
    setProgress(0)
    setPlaying(true)
  }

  const next = () => select((active + 1) % teasers.length)

  const toggleFullscreen = () => {
    const v = videoRef.current
    if (!v) return
    if (v.requestFullscreen) v.requestFullscreen()
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen()
  }

  return (
    <div className='showcase'>
      <div className='showcase__stage' ref={stageRef}>
        <div className={`showcase__frame ${playing ? '' : 'is-paused'}`}>
          <video
            key={current.id}
            ref={videoRef}
            className='showcase__video'
            src={current.video}
            muted={muted}
            playsInline
            preload='auto'
            onTimeUpdate={(e) =>
              setProgress(e.target.currentTime / (e.target.duration || 1))
            }
            onEnded={next}
            onClick={() => setPlaying(!playing)}
          />

          <div className='showcase__shade' aria-hidden='true' />

          <div className='showcase__caption'>
            <span className='venue-pill venue-pill--glass'>
              {current.venue}
            </span>
            <span className='showcase__caption-title'>{current.short}</span>
          </div>

          {!playing && (
            <button
              className='showcase__bigplay'
              onClick={() => setPlaying(true)}
              aria-label='Play'
            >
              <i className='fa-solid fa-play' />
            </button>
          )}

          <div className='showcase__controls'>
            <button
              onClick={() => setPlaying(!playing)}
              aria-label={playing ? 'Pause' : 'Play'}
            >
              <i className={`fa-solid ${playing ? 'fa-pause' : 'fa-play'}`} />
            </button>
            <button
              onClick={() => setMuted(!muted)}
              aria-label={muted ? 'Unmute' : 'Mute'}
            >
              <i
                className={`fa-solid ${
                  muted ? 'fa-volume-xmark' : 'fa-volume-high'
                }`}
              />
            </button>
            <button onClick={toggleFullscreen} aria-label='Full screen'>
              <i className='fa-solid fa-expand' />
            </button>
          </div>

          <div className='showcase__progress' aria-hidden='true'>
            <span style={{ transform: `scaleX(${progress})` }} />
          </div>
        </div>
      </div>

      <ol className='showcase__list' ref={listRef}>
        {teasers.map((t, i) => {
          const isActive = i === active
          return (
            <li key={t.id} className={isActive ? 'is-active' : ''}>
              <button className='showcase__item' onClick={() => select(i)}>
                <span className='showcase__index'>{pad(i)}</span>
                <span className='showcase__item-body'>
                  <span className='showcase__venue'>{t.venue}</span>
                  <span className='showcase__title'>{t.title}</span>
                  <span className='showcase__subtitle'>{t.subtitle}</span>
                </span>
                <span className='showcase__thumb'>
                  <video
                    src={`${t.video}#t=2`}
                    muted
                    playsInline
                    preload='metadata'
                    tabIndex={-1}
                  />
                </span>
              </button>

              <div className='showcase__details'>
                <div>
                  <p>{t.description}</p>
                  <div className='tag-row'>
                    {t.tags.map((tag) => (
                      <span className='tag' key={tag}>
                        {tag}
                      </span>
                    ))}
                  </div>
                  {t.link && (
                    <a
                      className='text-link'
                      href={t.link}
                      target='_blank'
                      rel='noreferrer'
                    >
                      Read the paper <i className='fa-solid fa-arrow-right' />
                    </a>
                  )}
                </div>
              </div>

              <span className='showcase__item-progress' aria-hidden='true'>
                <span
                  style={{ transform: `scaleX(${isActive ? progress : 0})` }}
                />
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export default TeaserShowcase
